import { call, put, select } from 'redux-saga/effects';

import * as types from './meeting-shortcuts.types';
import * as actions from './meeting-shortcuts.actions';
import * as api from '../../api/request-config/meeting-shortcuts.api';

import { IApiRequestConfig } from '../../api/api.types';
import { getShortcutFolderApi } from '../../api/request-config/meeting-shortcuts.api';
import { apiRequest } from '../../api/api.saga-services';
import { IMeeting } from '../meeting/meeting.types';
import { closeDialog } from '../../dialog/dialog.actions';
import { meetingFilesSelectedSelectors, meetingFilesSelectors } from './meeting-shortcuts.selectors';

export function* storeMeetingShortcuts(meetingShortcut: types.IMeetingFolder) {
  yield put(actions.storeMeetingShortcuts(meetingShortcut));
  return meetingShortcut;
}

export function* storeSelectedMeetingShortcutsFolder(
  meetingShortcutFolder: types.IMeetingFolder | types.IMeetingSubFolder,
) {
  yield put(actions.storeMeetingShortcutsFolder(meetingShortcutFolder));
}

export function* clearAllMeetingShortcuts() {
  const meetingShortcutIds: types.IMeetingFolder['id'][] = yield select(meetingFilesSelectors.selectResourceIds);
  const meetingShortcutSelectedIds: types.IMeetingFolder['id'][] = yield select(
    meetingFilesSelectedSelectors.selectResourceIds,
  );
  yield put(actions.clearMeetingShortcuts(meetingShortcutIds));
  yield put(actions.clearMeetingShortcutsFolder(meetingShortcutSelectedIds));
}

export function* clearSelectedMeetingShortcutsFolder() {
  yield put(actions.clearMeetingShortcutsFolder(null));
}

export function* clearMeetingShortcuts(meetingShortcutId: types.IMeetingFolder['id']) {
  yield put(actions.clearMeetingShortcuts(meetingShortcutId));
}

export function* createFileFolder(
  meetingId: IMeeting['id'],
  name: types.IMeetingFolder['name'],
  parentId?: types.IMeetingFolder['parentId'],
) {
  const newMeetingShortcutsFolder = yield call(apiRequest, api.createShortcutFolderApi({ meetingId, name, parentId }));

  if (!newMeetingShortcutsFolder) {
    return;
  }

  yield call(storeMeetingShortcuts, newMeetingShortcutsFolder);
  yield call(clearSelectedMeetingShortcutsFolder);
  yield put(closeDialog('meetingShortcutAddFolderDialog'));
  yield call(getShorcutFolders, meetingId);
}

export function* createFileFolderFile(
  meetingId: types.ICreateMeetingShortcut['meetingId'],
  externalLink: types.ICreateMeetingShortcut['externalLink'],
  description: types.ICreateMeetingShortcut['description'],
  folderId: types.IMeetingFolder['id'] | types.IMeetingSubFolder['id'],
  name: types.ICreateMeetingShortcut['name'],
) {
  const response = yield call(
    apiRequest,
    api.createShortcutFolderFileApi({ meetingId, externalLink, description, folderId, name }),
  );
  if (!response) {
    return;
  }

  yield put(closeDialog('meetingShortcutAddFileDialog'));
  yield call(getShorcutFolders, meetingId);
}

export function* updateShortcut(
  meetingId: types.IUpdateMeetingShortcut['meetingId'],
  shortcutId: types.IUpdateMeetingShortcut['shortcutId'],
  externalLink: types.IUpdateMeetingShortcut['externalLink'],
  description: types.IUpdateMeetingShortcut['description'],
  name: types.IUpdateMeetingShortcut['name'],
) {
  const response = yield call(
    apiRequest,
    api.updateShortcutFolderFileApi({ shortcutId, meetingId, externalLink, description, name }),
  );
  if (!response) {
    return;
  }

  yield put(closeDialog('meetingShortcutAddFileDialog'));
  yield call(getShorcutFolders, meetingId);
}

export function* deleteShortcut(
  meetingId: types.IDeleteMeetingShortcut['meetingId'],
  shortcutId: types.IDeleteMeetingShortcut['shortcutId'],
) {
  const response = yield call(apiRequest, api.deleteShortcutFolderFileApi({ shortcutId, meetingId }));
  if (!response) {
    return;
  }

  yield put(closeDialog('meetingShortcutAddFileDialog'));
  yield call(getShorcutFolders, meetingId);
}

export function* setSelectedMeetingShortcutsFolder(
  meetingId: IMeeting['id'],
  meetingShortcutFolder: types.IMeetingFolder | types.IMeetingSubFolder,
) {
  yield call(clearSelectedMeetingShortcutsFolder);
  if (meetingShortcutFolder.parentId) {
    const folderData = yield call(apiRequest, api.getShortcutFolderApi(meetingId, meetingShortcutFolder.id));
    yield call(storeSelectedMeetingShortcutsFolder, folderData);
  } else {
    yield call(storeSelectedMeetingShortcutsFolder, meetingShortcutFolder);
  }
  yield call(getShorcutFolders, meetingId);
}

export function* getShorcutFoldersAndUnSelectedMeetingShortcutsFolder(meetingId: IMeeting['id']) {
  yield call(clearSelectedMeetingShortcutsFolder);
  yield call(getShorcutFolders, meetingId);
}

export function* getShorcutFolders(meetingId: string) {
  const MeetingShortcutsFolders = yield call(apiRequest, api.getShorcutFoldersApi(meetingId));
  if (!MeetingShortcutsFolders) {
    return null;
  }
  for (const MeetingShortcutsFolder of MeetingShortcutsFolders) {
    const folderData = yield call(apiRequest, api.getShortcutFolderApi(meetingId, MeetingShortcutsFolder.id));
    yield call(storeMeetingShortcuts, folderData);
  }

  return MeetingShortcutsFolders;
}

export function* getFileFolder(
  meetingId: string,
  fileFolderId: string,
): Generator<unknown, types.IMeetingFolder, unknown> {
  const apiConfig: IApiRequestConfig<unknown> = getShortcutFolderApi(meetingId, fileFolderId);
  const folderData: types.IMeetingFolder = (yield call(apiRequest, apiConfig) as unknown) as types.IMeetingFolder;
  return folderData;
}

export function* deleteFileFolder(meetingId: string, fileFolderId: string): Generator<unknown, void, unknown> {
  yield put(closeDialog('meetingShortcutDeleteFileDialog'));
  yield call(getShorcutFolders, meetingId);
}
